/**
 * Shared money model for the whole SPA.
 *
 * One formatter, one currency vocabulary, consumed everywhere an amount is
 * shown (plans, overage, credit packs, ads budgets…). Backed by the multi-
 * currency API (ai-orchestrator) whose default is CAD. The user's chosen display
 * currency lives in `stores/currency-store` (persisted to localStorage) and is
 * read through the `useCurrency()` hook; this module is the pure, framework-free
 * part: the currency vocabulary + `formatMoney`.
 *
 * Symbols (locale `fr-FR`, the only locale that renders explicit regional money
 * symbols for all three): CAD → "$CA", USD → "$US", EUR → "€".
 */

/** The platform's default display currency — Karexia bills in Canadian dollars. */
import { tr, intlLocale } from '@/i18n';

export const DEFAULT_CURRENCY = 'cad';

/** Currencies the SPA knows how to label before the API tells us the live set. */
export const FALLBACK_CURRENCIES = ['cad', 'eur', 'usd'] as const;

export interface CurrencyMeta {
  /** Lower-case ISO-4217 code used on the wire (?currency=cad). */
  code: string;
  /** ISO-4217 upper-case code fed to Intl.NumberFormat. */
  iso: string;
  /** Short human label for the selector (e.g. "CAD — $CA"). */
  label: string;
}

/** Known currency metadata. Unknown codes are humanised on the fly. */
export const CURRENCY_META: Record<string, CurrencyMeta> = {
  cad: { code: 'cad', iso: tr('shell.lib.money.cad'), label: tr('shell.lib.money.cadCanadianDollarCa') },
  usd: { code: 'usd', iso: tr('shell.lib.money.usd'), label: tr('shell.lib.money.usdUsDollarUs') },
  eur: { code: 'eur', iso: 'EUR', label: tr('shell.lib.money.eurEuro') },
  gbp: { code: 'gbp', iso: 'GBP', label: tr('shell.lib.money.gbpPoundSterling') },
  aud: { code: 'aud', iso: 'AUD', label: tr('shell.lib.money.audAustralianDollarA') },
  chf: { code: 'chf', iso: 'CHF', label: tr('shell.lib.money.chfSwissFranc') },
};

/** The locale whose currency symbols match what the owner asked for. */
const MONEY_LOCALE = intlLocale('fr-FR');

/** Normalise any value to a lower-case currency code, defaulting to CAD. */
export function normalizeCurrency(value: unknown): string {
  if (typeof value === 'string' && value.trim()) return value.trim().toLowerCase();
  return DEFAULT_CURRENCY;
}

/** The display label for a currency code (falls back to the upper-cased code). */
export function currencyLabel(code: string): string {
  const c = normalizeCurrency(code);
  return CURRENCY_META[c]?.label ?? c.toUpperCase();
}

export interface FormatMoneyOptions {
  /** Max fraction digits. Default: 0 for whole amounts, else 2. */
  maximumFractionDigits?: number;
  /** Min fraction digits (default 0). */
  minimumFractionDigits?: number;
}

/**
 * Format `amount` (a major-unit number, e.g. 49 or 15.0) in `currency`.
 * Returns an em dash for nullish/non-finite input. Never throws — an unknown
 * ISO code degrades to "<amount> <CODE>".
 */
export function formatMoney(
  amount: number | null | undefined,
  currency: string,
  opts: FormatMoneyOptions = {},
): string {
  if (amount == null || !Number.isFinite(amount)) return '—';
  const code = normalizeCurrency(currency);
  const iso = CURRENCY_META[code]?.iso ?? code.toUpperCase();
  const maximumFractionDigits = opts.maximumFractionDigits ?? (amount % 1 === 0 ? 0 : 2);
  const minimumFractionDigits = opts.minimumFractionDigits ?? 0;
  try {
    return new Intl.NumberFormat(MONEY_LOCALE, {
      style: 'currency',
      currency: iso,
      maximumFractionDigits,
      minimumFractionDigits,
    }).format(amount);
  } catch {
    return `${amount.toFixed(maximumFractionDigits)} ${iso}`;
  }
}
